import { useId } from "react";

interface StoreIconProps {
  /** Rendered height in px. Width follows the glyph's own proportions. */
  height?: number;
  className?: string;
}

// Both viewBoxes are cropped to the glyph itself, so the two logos render at the
// same height and sit centered in whatever box they're placed in.
const APPLE = { x: 5.3, y: 4, w: 13.42, h: 16 };
const PLAY = { x: 7, y: 3, w: 24, h: 26 };

export function AppleIcon({ height = 20, className }: StoreIconProps) {
  return (
    <svg
      viewBox={`${APPLE.x} ${APPLE.y} ${APPLE.w} ${APPLE.h}`}
      width={(height * APPLE.w) / APPLE.h}
      height={height}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="M16.52 12.46C16.508 11.8438 16.6682 11.2365 16.9827 10.7065C17.2972 10.1765 17.7534 9.74476 18.3 9.46C17.9558 8.98143 17.5063 8.5883 16.9862 8.31089C16.466 8.03349 15.8892 7.87923 15.3 7.86C14.03 7.76 12.65 8.6 12.14 8.6C11.63 8.6 10.37 7.9 9.40999 7.9C7.40999 7.9 5.29999 9.49 5.29999 12.66C5.30963 13.6481 5.48194 14.6279 5.80999 15.56C6.24999 16.84 7.89999 20.05 9.61999 20C10.52 20 11.16 19.36 12.33 19.36C13.5 19.36 14.05 20 15.06 20C16.79 20 18.29 17.05 18.72 15.74C18.0689 15.4737 17.5119 15.0195 17.1201 14.4353C16.7282 13.8511 16.5193 13.1634 16.52 12.46ZM14.52 6.59C14.8307 6.23965 15.065 5.82839 15.2079 5.38245C15.3508 4.93651 15.3992 4.46569 15.35 4C14.4163 4.10239 13.5539 4.54785 12.93 5.25C12.6074 5.58991 12.3583 5.99266 12.1983 6.43312C12.0383 6.87358 11.9708 7.34229 12 7.81C12.4842 7.82361 12.9646 7.71974 13.3999 7.50728C13.8353 7.29482 14.2127 6.98009 14.5 6.59H14.52Z" />
    </svg>
  );
}

export function GooglePlayIcon({ height = 20, className }: StoreIconProps) {
  // Gradient ids must be unique per instance: the same button is rendered more than once on a
  // page (header + mobile menu), and a duplicate id inside a display:none copy blanks the icon.
  const id = useId().replace(/:/g, "");

  return (
    <svg
      viewBox={`${PLAY.x} ${PLAY.y} ${PLAY.w} ${PLAY.h}`}
      width={(height * PLAY.w) / PLAY.h}
      height={height}
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <defs>
        <linearGradient id={`${id}-blue`} x1="15.6769" y1="10.874" x2="7.07106" y2="19.5506" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00C3FF" />
          <stop offset="1" stopColor="#1BE2FA" />
        </linearGradient>
        <linearGradient id={`${id}-yellow`} x1="20.292" y1="15.8176" x2="31.7381" y2="15.8176" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFCE00" />
          <stop offset="1" stopColor="#FFEA00" />
        </linearGradient>
        <linearGradient id={`${id}-red`} x1="7.36932" y1="30.1004" x2="22.595" y2="17.8937" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DE2453" />
          <stop offset="1" stopColor="#FE3944" />
        </linearGradient>
        <linearGradient id={`${id}-green`} x1="8.10725" y1="1.90137" x2="22.5971" y2="13.7365" gradientUnits="userSpaceOnUse">
          <stop stopColor="#11D574" />
          <stop offset="1" stopColor="#01F176" />
        </linearGradient>
      </defs>
      <path fill={`url(#${id}-blue)`} d="M7.63473 28.5466L20.2923 15.8179L7.84319 3.29883C7.34653 3.61721 7 4.1669 7 4.8339V27.1664C7 27.7355 7.25223 28.2191 7.63473 28.5466Z" />
      <path fill={`url(#${id}-yellow)`} d="M30.048 14.4003C31.3169 15.0985 31.3169 16.9012 30.048 17.5994L24.9287 20.4165L20.292 15.8175L24.6923 11.4531L30.048 14.4003Z" />
      <path fill={`url(#${id}-red)`} d="M24.9292 20.4168L20.2924 15.8179L7.63477 28.5466C8.19139 29.0232 9.02389 29.1691 9.75635 28.766L24.9292 20.4168Z" />
      <path fill={`url(#${id}-green)`} d="M7.84277 3.29865L20.2919 15.8177L24.6922 11.4533L9.75583 3.23415C9.11003 2.87878 8.38646 2.95013 7.84277 3.29865Z" />
    </svg>
  );
}
